import { html, LitElement } from 'lit';

import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/menu-label/menu-label.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';

import { q1AlertBarStyles } from './q1-alert-bar-styles.js';

/*=== CONSTANTS ===*/

const MAX_TEXT_LENGTH = 100;

/*=== COMPONENT CLASS ===*/

export class Q1AlertBar extends LitElement {
  static styles = [ q1AlertBarStyles ];

  static properties = {

    /**
     * The variant of the alert bar. Options:
     * - `light`: Light variant with a white background. (Default)
     * - `dark`: Dark variant with a dark background.
     * - `themed`: Themed variant with a primary color background.
     * - `themed-secondary`: Themed variant with a secondary color background.
     */
    variant: { type: String, reflect: true },
    
    /**
     * The text content of the alert bar. Will be truncated if it exceeds MAX_TEXT_LENGTH.
     */
    message: { type: String },

    /**
     * The icon to display in the alert bar. To the left of the message.
     */
    icon: { type: String, reflect: true },

    /** Optionally specifies library to use for the icon. Defaults to `material`. */
    iconLibrary: { type: String, attribute: 'icon-library' },

    /**
     * The alert bar can always be snoozed, but when this property controls whether the member
     * can dismiss the alert bar permanently.
     * 
     * Defaults to `false` if not set.
     */
    dismissible: { type: Boolean, reflect: true },

    /** Including this enables the action button. The label for the alert action. */
    buttonLabel: { type: String, attribute: 'btn-label' },

    /** Including this adds an icon to the action button. */
    buttonIcon: { type: String, attribute: 'btn-icon' },
    
    /** Optionally specifies library to use for the button icon. Defaults to `material`. */
    buttonIconLibrary: { type: String, attribute: 'btn-icon-library' },

    /**
     * Passed to the button if included, rendering the button action 
     * as an anchor link with the specified path or URL.
     * Enables the buttonTarget property.
     */
    buttonHref: { type: String, attribute: 'btn-href' },

    /** Specifies the anchor target if included along with buttonHref */
    buttonTarget: { type: String, attribute: 'btn-target' },

    /**
     * This property controls whether the alert bar is open or closed. DO NOT SET IT if you want
     * to allow the alert bar to be snoozed or dismissed. Setting this property to `true` will
     * override the snooze and dismiss functionality.
     */
    open: { type: Boolean, reflect: true },

  };

  constructor() {
    super();

    this.variant = 'light';

    this.message = '';
    this.icon = null;
    this.iconLibrary = 'material';
    this.dismissible = false;
    this.buttonLabel = null;
    this.buttonIcon = null;
    this.buttonIconLibrary = 'material';
    this.buttonHref = null;
    this.buttonTarget = null;

    this.open = false;
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    this.checkIfOpen();
  }

  /*=== ACTION ===*/

  /**
   * This method re-evaluates whether the alert bar should be open or not based on the snooze
   * and dismiss state from local storage, then sets the `open` property.
   * 
   * It is called when the component is connected to the DOM, but can also be called manually.
   * 
   * Returns `true` if the alert bar is open, `false` otherwise.
   */
  checkIfOpen() {
    const isDismissed = this._getAlertDismissed() && this.dismissible;
    
    const snoozeUntilDate = this._getSnoozeUntilDate();
    const isSnoozed = snoozeUntilDate && snoozeUntilDate > new Date();
    if (!isSnoozed && snoozeUntilDate) this._clearSnoozeUntilDate();

    this.open = !isDismissed && !isSnoozed;

    return this.open;
  }

  snoozeUntil(snoozeUntilDate) {
    this._saveSnoozeUntilDate(snoozeUntilDate);
    this.open = false;
    this._showToast(
      'The alert has been snoozed until ' + snoozeUntilDate.toLocaleString(),
      'Alert Snoozed',
      'info',
    );
  }
  
  /*=== HANDLERS ===*/

  handleButtonClick(event) {
    if (this.isLinkButton) return true;

    event.preventDefault();
    event.stopPropagation();

    const clickEvent = new CustomEvent('q1-click', {
      detail: {}, bubbles: true, composed: true,
    });
    this.dispatchEvent(clickEvent);
  }

  handleSnoozeSelect(event) {
    const snoozeValue = event.detail?.item?.value || event.target?.value;
    let snoozeUntilDate = new Date();

    switch (snoozeValue) {
      case 'snooze-until-tomorrow':
        snoozeUntilDate.setDate(snoozeUntilDate.getDate() + 1);
        snoozeUntilDate.setHours(7, 0, 0, 0);
        this.snoozeUntil(snoozeUntilDate);
        break;
      
      case 'snooze-until-next-week':
        const day = snoozeUntilDate.getDay();
        const daysUntilNextMonday = (8 - day) % 7;
        snoozeUntilDate.setDate(snoozeUntilDate.getDate() + daysUntilNextMonday);
        snoozeUntilDate.setHours(7, 0, 0, 0);
        this.snoozeUntil(snoozeUntilDate);
        break;
      
      case 'snooze-until-next-month':
        snoozeUntilDate.setMonth(snoozeUntilDate.getMonth() + 1);
        snoozeUntilDate.setDate(1);
        snoozeUntilDate.setHours(7, 0, 0, 0);
        this.snoozeUntil(snoozeUntilDate);
        break;
      
      case 'dismiss':
        if (this.dismissible) this._saveAlertDismissed(true);
        this.open = false;
        this._showToast(
          'The alert has been permanently dismissed.',
          'Alert Dismissed',
          'info',
        );
        break;
    }
  }

  /*=== GETTERS ===*/

  get truncatedMessage() {
    if (!this.message) return '';

    return this.message.length > MAX_TEXT_LENGTH
      ? `${this.message.slice(0, MAX_TEXT_LENGTH)}...`
      : this.message;
  }

  get baseClasses() {
    return [
      `${this.variant}-variant`,
      this.dismissible ? 'dismissible' : null,
    ].filter(c => c).join(' ');
  }

  get buttonVariant() {
    return 'default';
  }

  get isLinkButton() {
    return this.buttonHref?.length;
  }

  /** Returns a key combining the name of this component and the current view path. */
  get baseLocalStorageKey() {
    const currentPath = window.location.pathname;
    
    return `q1-alert-bar:${currentPath}`;
  }
  
  /*=== TEMPLATES ===*/

  render() {
    if (!this.open) return null;
    
    return html`
      <alert-base part="base" class=${this.baseClasses}>
        <div id="content" part="content">
          ${this.iconTemplate}
          ${this.messageTemplate}
        </div>

        <div id="buttons" part="buttons">
          ${this.snoozeButtonTemplate}
          ${this.actionButtonTemplate}
        </div>
      </alert-base>
    `;
  }

  get messageTemplate() {
    return html`
      <div id="message" part="message">${this.truncatedMessage}</div>
    `;
  }

  get iconTemplate() {
    if (!this.icon) return null;

    return html`
      <sl-icon
        id="icon"
        part="icon"
        library=${this.iconLibrary}
        name=${this.icon}
      ></sl-icon>
    `;
  }

  get snoozeButtonTemplate() {
    return html`
      <sl-dropdown
        id="snooze-dropdown"
        part="snooze-dropdown"
        @sl-select=${this.handleSnoozeSelect}
      >
        <sl-icon-button
          id="snooze-button"
          library="material"
          name="schedule"
          slot="trigger"
          part="snooze-button"
          size="small"
          label="Snooze"
        ></sl-icon-button>

        <sl-menu part="snooze-menu">
          <sl-menu-label>Snooze until...</sl-menu-label>
          <sl-menu-item value="snooze-until-tomorrow">Tomorrow</sl-menu-item>
          <sl-menu-item value="snooze-until-next-week">Next week</sl-menu-item>
          <sl-menu-item value="snooze-until-next-month">Next month</sl-menu-item>
          
          ${!this.dismissible ? null : html`
            <sl-divider></sl-divider>
            <sl-menu-item value="dismiss">Dismiss Forever</sl-menu-item>
          `}
        </sl-menu>
      </sl-dropdown>
    `;
  }

  get actionButtonTemplate() {
    if (!this.buttonLabel) return null;

    return html`
      <sl-button
        id="action-button"
        part="action-button"
        size="small"
        href=${this.buttonHref}
        target=${this.buttonTarget}
        @click=${this.handleButtonClick}
        variant=${this.buttonVariant}
      >
        ${!this.buttonIcon ? null : html`
          <sl-icon
            slot="prefix"
            library=${this.buttonIconLibrary}
            name=${this.buttonIcon}
          ></sl-icon>
        `}
        ${this.buttonLabel}
      </sl-button>
    `;
  }

  /*=== UTILITY METHODS ===*/

  _saveSnoozeUntilDate(snoozeUntilDate) {
    const snoozeUntilString = snoozeUntilDate.toISOString();
    const localStorageKey = `${this.baseLocalStorageKey}:snooze-until`;
    localStorage.setItem(localStorageKey, snoozeUntilString);
  }
  
  _getSnoozeUntilDate() {
    const localStorageKey = `${this.baseLocalStorageKey}:snooze-until`;
    const snoozeUntilString = localStorage.getItem(localStorageKey);
    if (!snoozeUntilString) return null;
    
    const snoozeUntilDate = new Date(snoozeUntilString);
    return snoozeUntilDate;
  }

  _clearSnoozeUntilDate() {
    const localStorageKey = `${this.baseLocalStorageKey}:snooze-until`;
    localStorage.removeItem(localStorageKey);
  }

  _saveAlertDismissed(booleanValue) {
    const localStorageKey = `${this.baseLocalStorageKey}:dismissed`;
    localStorage.setItem(localStorageKey, booleanValue ? 'true' : 'false');
  }

  _getAlertDismissed() {
    const localStorageKey = `${this.baseLocalStorageKey}:dismissed`;
    const booleanValue = localStorage.getItem(localStorageKey);
    return booleanValue === 'true';
  }

  _showToast(message, title, variant, duration) {
    const event = new CustomEvent('show-toast', {
      detail: { message, title, variant, duration },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

}

customElements.define('q1-alert-bar', Q1AlertBar);
